/* eslint-disable no-console */
import React, { ComponentType, ReactElement } from 'react';
import LazyLoad from 'react-lazyload';
import URL from 'url';
import styled, { css } from 'styled-components';

import { ALLOWED_IMAGE_TYPE, ALLOWED_SIZES, DEFAULT_IMAGE_SIZE } from 'shared/utils/imageSizes';
import { Loader as DefaultLoader } from 'shared/components/Loader';

// ImageV2 is different from Image in that ImageV2 uses the new image service 'go-image-v2'
// go-image-v2 API Docs:
// https://github.com/goatapp/go-image-v2/blob/master/README.md#http-api-documentation
export interface IImageProps {
  alpha?: boolean;
  alt: string;
  background?: string; // RGB value to use for transparent PNGs, ex: background=255,255,255
  className?: string;
  crop?: boolean;
  disableLazyLoad?: boolean;
  getImageHost?: (host?: string) => string;
  height?: number;
  loader?: ComponentType;
  missingAssetPath?: string;
  noSrcModification?: boolean;
  onClick?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  resizeable?: boolean;
  size?: number; // takes the longest edge of the image and uses that as the resizing value
  sizes?: string;
  src: string;
  type?: string; // overrides content negotiation based on Accepts header, options: jpeg, png, webp
  width?: number;
  quality?: number; // JPEG and WEBP image quality between 1-100 and divisible by 5. Defaults to 85
}

export const BaseImage = (props: IImageProps): ReactElement => {
  const {
    alpha,
    alt,
    background,
    className,
    crop = false,
    disableLazyLoad,
    getImageHost,
    height,
    loader: Loader = DefaultLoader,
    missingAssetPath,
    noSrcModification,
    onClick,
    onError,
    onLoad,
    quality,
    resizeable = true,
    size,
    sizes,
    src = 'missing.png',
    type,
    width,
  } = props;

  if (!sizes) {
    console.warn(
      // eslint-disable-next-line max-len
      'Warning: no "sizes" prop detected on <Image /> component.',
    );
  }

  interface IGetImagePathname {
    pathname: string;
    forceCrop: boolean;
    sizeWidth?: number;
  }
  const getImageUrl = ({
    pathname,
    forceCrop,
    sizeWidth = DEFAULT_IMAGE_SIZE,
  }: IGetImagePathname) => {
    if (/missing.png/.test(pathname)) {
      return {
        path: '/placeholders/dash.png',
        query: '',
      };
    }

    const splitPathname = pathname.split('/');
    const shouldCrop = forceCrop || splitPathname[1] === 'crop';

    // filter out crop and sizeInstruction from pathname
    const reconstructedPathname = splitPathname
      .filter((pathnameSegment, index) => {
        const isSizeInstruction =
          index <= 2 && ALLOWED_SIZES.includes(Number.parseInt(pathnameSegment, 10));
        return pathnameSegment && pathnameSegment !== 'crop' && !isSizeInstruction;
      })
      .join('/');

    const query = [
      `${shouldCrop ? `action=crop` : ''}`,
      `${sizeWidth && !width && ALLOWED_SIZES.includes(sizeWidth) ? `width=${sizeWidth}` : ''}`,
      `${width && ALLOWED_SIZES.includes(width) ? `width=${width}` : ''}`,
      `${height && ALLOWED_SIZES.includes(height) ? `height=${height}` : ''}`,
      `${size && ALLOWED_SIZES.includes(size) ? `size=${size}` : ''}`,
      `${type && ALLOWED_IMAGE_TYPE.includes(type.toLowerCase()) ? `type=${type}` : ''}`,
      `${background ? `background=${background}` : ''}`,
      `${alpha?.toString() ? `alpha=${alpha.toString()}` : ''}`,
      `${quality ? `quality=${quality}` : ''}`,
    ]
      .filter(Boolean)
      // query must be in ABC-order (for CDN caching) or else the API will reject it
      .sort()
      .join('&')
      // the API is enforcing lowercase for query params (for CDN caching)
      .toLowerCase();

    // go-image-v2 service is prefixed with `/transform/v1`
    return {
      path: `/transform/v1/${reconstructedPathname}`,
      query,
    };
  };

  /** Flag that determines if no asset image is present. */
  const isMissing = /missing.png/.test(src || '');

  interface IImageSrcs {
    src: string;
    srcSet?: string;
  }

  /**
   * Creates the source props used by <Image />
   *
   * @returns {{ src: string, srcSet: string }} src props spread on <StyledImage />
   */
  const getImageSrcs = (): IImageSrcs => {
    if (noSrcModification) {
      return { src };
    }

    const url = URL.parse(src || 'missing.png');
    const originalPathname = url.pathname;
    url.protocol = 'https'; // request all images with https

    if (typeof getImageHost === 'function') {
      url.host = getImageHost(url.host); // get correct CDN host if applicable
    }

    // non-resizeable images are routed to the correct CDN
    // without changing the crop or size
    if (!resizeable && originalPathname) {
      const pathnameSegments = originalPathname.split('/');
      let sizeWidth = DEFAULT_IMAGE_SIZE;

      // get the size from the pathname
      if (ALLOWED_SIZES.includes(Number.parseInt(pathnameSegments[1], 10))) {
        sizeWidth = Number.parseInt(pathnameSegments[1], 10);
      } else if (ALLOWED_SIZES.includes(Number.parseInt(pathnameSegments[2], 10))) {
        // size is the 2nd index when it has crop in pathname, ex: /crop/3500/image.png
        sizeWidth = Number.parseInt(pathnameSegments[2], 10);
      }

      const imageUrl = getImageUrl({
        pathname: originalPathname,
        forceCrop: false,
        sizeWidth,
      });

      url.pathname = imageUrl.path;
      url.search = imageUrl.query;

      return {
        src: URL.format(url),
      };
    }

    // src is a backup for browsers without srcset
    // its path is updated with the crop prop and default size as query params
    if (originalPathname) {
      const imageUrl = getImageUrl({
        pathname: originalPathname,
        forceCrop: crop,
      });

      url.pathname = imageUrl.path;
      url.search = imageUrl.query;
    }

    // srcSet lets the browser pick the best size from ALLOWED_SIZES
    const imageSrcs: IImageSrcs = {
      src: URL.format(url),
      srcSet: ALLOWED_SIZES.map((sizeNum) => {
        if (originalPathname) {
          const imageUrl = getImageUrl({
            pathname: originalPathname,
            forceCrop: crop,
            sizeWidth: sizeNum,
          });

          url.pathname = imageUrl.path;
          url.search = imageUrl.query;
        }
        return `${URL.format(url)} ${sizeNum}w`;
      }).join(', '),
    };

    // Custom image src and srcset object if no actual asset to display.
    // It sets src as a 1x1 transparent image.
    const missingSrcs = {
      src: 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==',
      srcSet: '',
    };

    return isMissing && missingAssetPath ? missingSrcs : imageSrcs;
  };

  const imageSrcs = getImageSrcs();

  const ImageLoader = Loader ? (
    <BaseImage.LoaderWrapper>
      <Loader />
    </BaseImage.LoaderWrapper>
  ) : null;

  const baseImage = (
    <BaseImage.StyledImage
      {...props}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={onError}
      onLoad={onLoad}
      sizes={sizes}
      {...imageSrcs}
      missingAssetPath={missingAssetPath}
      isMissing={isMissing}
    />
  );

  if (disableLazyLoad) {
    return baseImage;
  }

  return (
    <LazyLoad once placeholder={ImageLoader}>
      {baseImage}
    </LazyLoad>
  );
};

// images use objectFit contain
// to support responsive / client sizes
// that differ from the delivered image
BaseImage.StyledImage = styled.img<{
  missingAssetPath?: string;
  isMissing: boolean;
}>`
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  // Sets a centered missing asset as background if product image is missing
  ${({ missingAssetPath, isMissing }) => {
    if (missingAssetPath && isMissing) {
      return css`
        background: url(${missingAssetPath}) no-repeat center;
      `;
    }
  }}
`;
BaseImage.LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
